export type MasonryInput = {
  /** width / height of each item, in display order. */
  aspects: readonly number[];
  containerWidth: number;
  columns: number;
  gap: number;
  /** Fixed height added below each item (e.g. a visible caption). */
  extraHeight?: number;
};

export type MasonryBox = { x: number; y: number; width: number; height: number; column: number };

export type MasonryLayout = { boxes: MasonryBox[]; height: number; columnWidth: number };

/**
 * Shortest-column masonry. Every item has the same width; heights follow each item's aspect
 * ratio, so the layout can be computed before any image loads. Items are placed in order into
 * whichever column is currently shortest (leftmost on ties).
 */
export function layoutMasonry({
  aspects,
  containerWidth,
  columns,
  gap,
  extraHeight = 0,
}: MasonryInput): MasonryLayout {
  const cols = Math.max(1, Math.floor(columns));
  const columnWidth = Math.max(0, (containerWidth - gap * (cols - 1)) / cols);
  const heights = new Array<number>(cols).fill(0);

  const boxes = aspects.map((aspect) => {
    let column = 0;
    for (let c = 1; c < cols; c++) if (heights[c]! < heights[column]!) column = c;

    const height = columnWidth / (aspect > 0 ? aspect : 1) + extraHeight;
    const box = {
      x: column * (columnWidth + gap),
      y: heights[column]!,
      width: columnWidth,
      height,
      column,
    };
    heights[column]! += height + gap;
    return box;
  });

  const height = Math.max(0, Math.max(...heights) - (aspects.length ? gap : 0));
  return { boxes, height, columnWidth };
}

/** Column count for a container width: 3 on desktop, 2 on tablet, 1 on phones. */
export function columnsFor(width: number): number {
  if (width >= 960) return 3;
  if (width >= 560) return 2;
  return 1;
}
